import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Chrome } from '@/components/Chrome';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import type { PublishScheduleInput } from '@/lib/gql/__generated__/graphql';
import { useSurfaceAccess } from '@/lib/platform/surfaces';
import { useSchedule, useScheduleRef } from './state';

type PublishInput = Omit<PublishScheduleInput, 'scheduleId'>;

const initialForm: PublishInput = {
  route: '',
  direction: '',
  serviceDay: '',
  tripId: '',
  stopId: '',
  stopName: '',
  scheduledTime: undefined,
  tripDestination: undefined,
};

const displayValue = (value: string | null | undefined) => value?.trim() || 'Not recorded';

export function ScheduleDataPage() {
  const access = useSurfaceAccess(SURFACES['schedule-data']);
  const schedule = useSchedule((snapshot) => snapshot);
  const scheduleRef = useScheduleRef();
  const [form, setForm] = useState<PublishInput>(initialForm);

  useEffect(() => {
    if (access === 'allowed') scheduleRef.send({ type: 'schedule.load-available' });
  }, [access, scheduleRef]);

  const updateField = (field: keyof PublishInput, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const requiredFieldsComplete =
    form.route.trim() !== '' &&
    form.direction.trim() !== '' &&
    form.serviceDay.trim() !== '' &&
    form.tripId.trim() !== '' &&
    form.stopId.trim() !== '' &&
    form.stopName.trim() !== '';

  const publish = () => {
    scheduleRef.send({
      type: 'schedule.publish',
      input: {
        route: form.route.trim(),
        direction: form.direction.trim(),
        serviceDay: form.serviceDay.trim(),
        tripId: form.tripId.trim(),
        stopId: form.stopId.trim(),
        stopName: form.stopName.trim(),
        scheduledTime: form.scheduledTime?.trim() || undefined,
        tripDestination: form.tripDestination?.trim() || undefined,
      },
    });
  };

  if (access === 'waiting') {
    return <AccessState title="Checking schedule data access…" />;
  }

  if (access === 'unavailable') {
    return <AccessState title="Schedule data access is unavailable." />;
  }

  if (access === 'denied') {
    return <AccessState title="You do not have access to this schedule data screen." />;
  }

  const isLoading = schedule.matches({ available: 'loading' });
  const isReadError = schedule.matches({ available: 'error' });
  const isSaving = schedule.matches({ write: 'saving' });
  const schedules = schedule.context.schedules;

  return (
    <Chrome>
      <div className="space-y-8">
        <section className="space-y-2">
          <p className="text-sm font-medium uppercase tracking-[0.16em] text-primary">Maintainer workspace</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">Schedule data</h1>
          <p className="max-w-2xl text-muted-foreground">
            Publish one recorded arrival at a time for RapidRide E Line or Route 44. Scheduled times are local Seattle
            times and may be left unrecorded.
          </p>
        </section>

        <Card>
          <CardHeader>
            <CardTitle>Publish a schedule record</CardTitle>
            <CardDescription>
              Enter the service-day details exactly as recorded. Schedule IDs are assigned automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <ScheduleField
                id="publish-schedule-route"
                label="Route"
                value={form.route}
                onChange={(value) => updateField('route', value)}
                placeholder="RapidRide E Line or 44"
              />
              <ScheduleField
                id="publish-schedule-direction"
                label="Direction"
                value={form.direction}
                onChange={(value) => updateField('direction', value)}
                placeholder="Northbound"
              />
              <ScheduleField
                id="publish-schedule-serviceDay"
                label="Service day"
                value={form.serviceDay}
                onChange={(value) => updateField('serviceDay', value)}
                placeholder="Weekday"
              />
              <ScheduleField
                id="publish-schedule-tripId"
                label="Trip ID"
                value={form.tripId}
                onChange={(value) => updateField('tripId', value)}
              />
              <ScheduleField
                id="publish-schedule-stopId"
                label="Stop ID"
                value={form.stopId}
                onChange={(value) => updateField('stopId', value)}
              />
              <ScheduleField
                id="publish-schedule-stopName"
                label="Stop name"
                value={form.stopName}
                onChange={(value) => updateField('stopName', value)}
              />
              <ScheduleField
                id="publish-schedule-scheduledTime"
                label="Scheduled time (optional)"
                value={form.scheduledTime ?? ''}
                onChange={(value) => updateField('scheduledTime', value)}
                placeholder="08:15"
              />
              <ScheduleField
                id="publish-schedule-tripDestination"
                label="Trip destination (optional)"
                value={form.tripDestination ?? ''}
                onChange={(value) => updateField('tripDestination', value)}
              />
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button
                type="button"
                data-jr-key="publish-schedule"
                onClick={publish}
                disabled={isSaving || !requiredFieldsComplete}
                className="min-h-11"
              >
                {isSaving ? 'Publishing…' : 'Publish schedule'}
              </Button>
              {schedule.context.message && <p className="text-sm text-destructive">{schedule.context.message}</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Published schedule</CardTitle>
            <CardDescription>Recorded arrivals currently available to Seattle Bus Times.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && (
              <p className="py-8 text-center text-sm text-muted-foreground">Loading published schedule data…</p>
            )}
            {isReadError && (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <p className="text-sm text-destructive">Published schedule data could not be loaded.</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => scheduleRef.send({ type: 'schedule.load-available' })}
                  className="min-h-11"
                >
                  Try again
                </Button>
              </div>
            )}
            {schedule.matches({ available: 'ready' }) && schedules.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No schedule records have been published yet.
              </p>
            )}
            {schedule.matches({ available: 'ready' }) && schedules.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Route</TableHead>
                      <TableHead>Direction</TableHead>
                      <TableHead>Service day</TableHead>
                      <TableHead>Trip ID</TableHead>
                      <TableHead>Stop</TableHead>
                      <TableHead>Scheduled time</TableHead>
                      <TableHead>Destination</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedules.map((item) => (
                      <TableRow key={item.scheduleId ?? `${item.route}-${item.tripId}-${item.stopId}`}>
                        <TableCell className="font-medium">{displayValue(item.route)}</TableCell>
                        <TableCell>{displayValue(item.direction)}</TableCell>
                        <TableCell>{displayValue(item.serviceDay)}</TableCell>
                        <TableCell>{displayValue(item.tripId)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{displayValue(item.stopName)}</div>
                          <div className="text-xs text-muted-foreground">{displayValue(item.stopId)}</div>
                        </TableCell>
                        <TableCell className="tabular-nums">{displayValue(item.scheduledTime)}</TableCell>
                        <TableCell>{displayValue(item.tripDestination)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Chrome>
  );
}

function ScheduleField({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        data-jr-key={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="min-h-11"
      />
    </div>
  );
}

function AccessState({ title }: { title: string }) {
  return (
    <Chrome>
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-sm text-muted-foreground">{title}</p>
        </CardContent>
      </Card>
    </Chrome>
  );
}
